/**
 * Date Formatting Utilities
 * Provides human-readable date formatting functions using date-fns
 */

import { formatDistanceToNow, format, isValid, parseISO, isYesterday } from 'date-fns';

/**
 * Parse a date string or Date object safely
 */
function parseDate(dateInput: string | Date): Date | null {
    try {
        if (dateInput instanceof Date) {
            return isValid(dateInput) ? dateInput : null;
        }
        const parsedDate = parseISO(dateInput);
        return isValid(parsedDate) ? parsedDate : null;
    } catch {
        return null;
    }
}

/**
 * Format a date string to a human-readable relative time or absolute date
 * Examples:
 * - "just now" (< 1 min ago)
 * - "5 minutes ago"
 * - "2 hours ago"
 * - "Yesterday at 3:30 PM"
 * - "Oct 11, 2024 at 3:30 PM"
 */
export function formatDateHuman(dateString: string | Date): string {
    try {
        const date = parseDate(dateString);

        if (!date) {
            console.warn("Invalid date provided to formatDateHuman:", dateString);
            return "Invalid date";
        }

        const now = new Date();
        const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

        // Less than 1 minute
        if (diffInSeconds < 60) {
            return "just now";
        }

        // Less than 1 hour
        if (diffInSeconds < 3600) {
            return formatDistanceToNow(date, { addSuffix: true });
        }

        // Less than 24 hours
        if (diffInSeconds < 86400) {
            return formatDistanceToNow(date, { addSuffix: true });
        }

        // Yesterday
        if (isYesterday(date)) {
            return `Yesterday at ${format(date, 'h:mm a')}`;
        }

        // Less than 7 days
        if (diffInSeconds < 604800) {
            return formatDistanceToNow(date, { addSuffix: true });
        }

        // Older than 7 days - show full date and time
        return format(date, 'MMM d, yyyy \'at\' h:mm a');
    } catch (error) {
        console.error("Error formatting date:", error, dateString);
        return "Invalid date";
    }
}

/**
 * Format a date string to a compact format
 * Examples:
 * - "5m ago"
 * - "2h ago"
 * - "3d ago"
 * - "Oct 11, 2024"
 */
export function formatDateCompact(dateString: string | Date): string {
    try {
        const date = parseDate(dateString);

        if (!date) {
            console.warn("Invalid date provided to formatDateCompact:", dateString);
            return "Invalid date";
        }

        const now = new Date();
        const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

        // Less than 1 minute
        if (diffInSeconds < 60) {
            return "now";
        }

        // Less than 1 hour
        if (diffInSeconds < 3600) {
            const minutes = Math.floor(diffInSeconds / 60);
            return `${minutes}m ago`;
        }

        // Less than 24 hours
        if (diffInSeconds < 86400) {
            const hours = Math.floor(diffInSeconds / 3600);
            return `${hours}h ago`;
        }

        // Less than 7 days
        if (diffInSeconds < 604800) {
            const days = Math.floor(diffInSeconds / 86400);
            return `${days}d ago`;
        }

        // Older - show date only
        return format(date, 'MMM d, yyyy');
    } catch (error) {
        console.error("Error formatting compact date:", error, dateString);
        return "Invalid date";
    }
}

/**
 * Format a date string to full absolute date and time
 * Example: "October 11, 2024 at 3:30 PM"
 */
export function formatDateFull(dateString: string | Date): string {
    try {
        const date = parseDate(dateString);

        if (!date) {
            console.warn("Invalid date provided to formatDateFull:", dateString);
            return "Invalid date";
        }

        return format(date, 'MMMM d, yyyy \'at\' h:mm a');
    } catch (error) {
        console.error("Error formatting full date:", error, dateString);
        return "Invalid date";
    }
}
